import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from 'urql'
import { Customer } from '../../../gql/graphql'
import { compact } from '../../_helpers/compact'
import { APP_BASE_PATH } from '../../_lib/festival'
import { joinFestivalMutation, leaveFestivalMutation } from '../../_lib/graphql/mutations'
import { discoverableFestivalsQuery, myFestivalsQuery } from '../../_lib/graphql/queries'
import { PortalBrand } from '../PortalBrand'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'
import { useMyself } from '../UserContext'
import { FestivalCard } from '../FestivalCard'
import { useFestival } from '../FestivalContext/useFestival'
import { FestivalSection } from './FestivalSection'

export const FestivalSelection = () => {
  const { logout, refetchUser } = useMyself() || {}
  const { selectFestival } = useFestival() || {}
  const { pushError } = useSnackbarPush()

  const [myFestivalsResult, refetchMyFestivals] = useQuery({
    query: myFestivalsQuery,
    requestPolicy: 'network-only'
  })
  const [discoverableResult, refetchDiscoverable] = useQuery({
    query: discoverableFestivalsQuery,
    requestPolicy: 'network-only'
  })
  const [joinState, executeJoin] = useMutation(joinFestivalMutation)
  const [leaveState, executeLeave] = useMutation(leaveFestivalMutation)

  const myFestivals = useMemo(() => compact(myFestivalsResult.data?.myFestivals) as Customer[], [myFestivalsResult.data])
  const discoverableFestivals = useMemo(() => compact(discoverableResult.data?.discoverableFestivals) as Customer[], [discoverableResult.data])

  const handleJoin = useCallback((festival: Customer) => {
    executeJoin({ input: { id: festival.id } }).then(result => {
      if (result.error) {
        pushError(result.error.message)
        return
      }

      window.location.replace(APP_BASE_PATH)
    })
  }, [executeJoin, pushError])

  const handleLeave = useCallback((festival: Customer) => {
    if (!window.confirm(`Möchtest du "${festival.title}" wirklich verlassen? Deine Gruppenzugehörigkeiten in diesem Festival werden entfernt.`)) {
      return
    }

    executeLeave({ input: { id: festival.id } }).then(result => {
      if (result.error) {
        pushError(result.error.message)
        return
      }

      refetchUser?.()
      refetchMyFestivals({ requestPolicy: 'network-only' })
      refetchDiscoverable({ requestPolicy: 'network-only' })
    })
  }, [executeLeave, pushError, refetchDiscoverable, refetchMyFestivals, refetchUser])

  return (
    <div className={'h-full overflow-y-auto bg-sand'}>
      <div className={'mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8'}>
        <header className={'flex items-center justify-between gap-4'}>
          <PortalBrand />
          {logout && (
            <button
              type={'button'}
              onClick={logout}
              className={'rounded-full px-4 py-2 text-sm font-semibold text-primary-200 hover:bg-sand-200 focus-visible:outline-2 focus-visible:outline-primary'}
            >
              Abmelden
            </button>
          )}
        </header>

        <div className={'mt-12 mb-12 max-w-2xl'}>
          <h1 className={'text-4xl font-bold tracking-tight text-primary-200 text-balance sm:text-5xl'}>
            Welches Festival möchtest du öffnen?
          </h1>
          <p className={'mt-4 text-base leading-7 text-gray-600'}>
            Du kannst jederzeit über dein Profilmenü zwischen deinen Festivals wechseln.
          </p>
        </div>

        <FestivalSection
          title={'Meine Festivals'}
          description={'Hier bist du bereits dabei.'}
          emptyText={'Du bist noch keinem Festival beigetreten. Tritt unten einem Festival bei.'}
          loading={myFestivalsResult.fetching && !myFestivalsResult.data}
          isEmpty={myFestivals.length === 0}
        >
          {myFestivals.map(festival => (
            <FestivalCard
              key={festival.id}
              festival={festival}
              actions={(
                <>
                  <button
                    type={'button'}
                    onClick={() => selectFestival?.(festival.id)}
                    className={'inline-flex items-center justify-center rounded-full bg-primary-200 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
                  >
                    Öffnen
                  </button>
                  <button
                    type={'button'}
                    disabled={leaveState.fetching}
                    onClick={() => handleLeave(festival)}
                    className={'rounded-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-sand-100 hover:text-secondary disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-primary'}
                  >
                    Verlassen
                  </button>
                </>
              )}
            />
          ))}
        </FestivalSection>

        <FestivalSection
          title={'Weitere Festivals'}
          description={'Tritt einem Festival bei, um dort mitzuarbeiten.'}
          emptyText={'Zurzeit gibt es keine weiteren Festivals, denen du beitreten kannst.'}
          loading={discoverableResult.fetching && !discoverableResult.data}
          isEmpty={discoverableFestivals.length === 0}
        >
          {discoverableFestivals.map(festival => (
            <FestivalCard
              key={festival.id}
              festival={festival}
              actions={(
                <button
                  type={'button'}
                  disabled={joinState.fetching}
                  onClick={() => handleJoin(festival)}
                  className={'inline-flex items-center justify-center rounded-full border border-primary-200 px-6 py-2.5 text-sm font-semibold text-primary-200 hover:bg-primary-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
                >
                  Beitreten
                </button>
              )}
            />
          ))}
        </FestivalSection>
      </div>
    </div>
  )
}
